/**
 * Straiv Premium — product matrix (single source of truth for client gates).
 *
 * Subscription state lives on `profiles` (see migration `premium_subscriptions`):
 *   - subscription_tier: 'free' | 'premium'
 *   - subscription_expires_at: null = no expiry / lifetime; otherwise must be future
 *   - apple_original_transaction_id, subscription_source — for App Store wiring later
 *
 * Server enforcement: Supabase edge `_shared/rateLimiter.ts` + `_shared/subscription.ts`
 * (coach chat weekly, food photo daily, misc AI daily). Client gates UX only.
 *
 * App Store: add products in App Store Connect, then implement StoreKit 2 (or RevenueCat)
 * and a small edge function to verify transactions and UPDATE profiles.
 */

export type ProfileForPremium = {
  subscription_tier?: string | null;
  subscription_expires_at?: string | null;
};

export const FREE_COACH_MESSAGES_PER_WEEK = 5;
export const FREE_FOOD_PHOTO_SCANS_PER_DAY = 3;
export const FREE_ANALYTICS_HISTORY_DAYS = 30;
export const PREMIUM_ANALYTICS_HISTORY_DAYS = 3650;

export const FREE_MAX_ACTIVE_WORKOUT_PROGRAMS = 1;
export const FREE_MAX_ACTIVE_GOALS = 1;

/** Share templates included on the free plan (Classic + transparent-style minimal). */
export type ShareTemplateId =
  | 'classic'
  | 'minimal'
  | 'bold'
  | 'route'
  | 'splits'
  | 'splitsTransparent'
  | 'gymExercises'
  | 'gymSimple';

export const SHARE_TEMPLATES_FREE = new Set<ShareTemplateId>(['classic', 'minimal']);

export function isShareTemplatePremiumOnly(template: ShareTemplateId): boolean {
  return !SHARE_TEMPLATES_FREE.has(template);
}

export function isPremiumPreviewFromStorage(): boolean {
  if (typeof window === 'undefined') return false;
  if (import.meta.env.VITE_PREMIUM_PREVIEW === '1') return true;
  if (import.meta.env.DEV && window.localStorage?.getItem('straiv_premium_preview') === '1') {
    return true;
  }
  return false;
}

export function isPremiumActive(profile: ProfileForPremium | null | undefined): boolean {
  if (isPremiumPreviewFromStorage()) return true;
  if (!profile || profile.subscription_tier !== 'premium') return false;
  if (!profile.subscription_expires_at) return true;
  return new Date(profile.subscription_expires_at).getTime() > Date.now();
}

export function analyticsHistoryDays(isPremium: boolean): number {
  return isPremium ? PREMIUM_ANALYTICS_HISTORY_DAYS : FREE_ANALYTICS_HISTORY_DAYS;
}

export function countActiveWorkoutPrograms(
  programs: { status: string }[],
): number {
  return programs.filter((p) => p.status === 'active').length;
}
